import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { successToastMessage, errorToastMessage, infoToastMessage } from "../../../../../utils/toastMessage";
import {
  SECTIONS,
  NAMEKEYS,
  CHIP,
  hydrateConfig,
  dehydrateConfig,
  diffAll,
  sameObj,
  cloneObj,
  r6,
  isNum,
  pctTxt,
  fmtVal,
  sampleConfig,
} from "./promoRadarData";
import { commonService } from "../../../../../utils/commonService";

export default function PromoConfigModal({
  show,
  onClose,
  config,
  baseInfo,
  hotelInfo,
  dataKeys,
  isLoading = false,
  onSave,
  onPreviewApply,
}) {
  const [draft, setDraft] = useState(() => hydrateConfig(config || sampleConfig));
  const [saved, setSaved] = useState(() => cloneObj(draft));
  const defaults = useMemo(() => {
    const base = {
      ...sampleConfig,
      channels:
        config?.channels && config.channels.length > 0
          ? config.channels.map((c) => ({
              ...c,
              commission: c.direct ? 0 : 0.18,
            }))
          : sampleConfig.channels,
      promotionNames:
        config?.promotionNames && config.promotionNames.length > 0
          ? config.promotionNames
          : sampleConfig.promotionNames,
    };
    return hydrateConfig(base);
  }, [config]);
  const [activeTab, setActiveTab] = useState(SECTIONS[0].id);
  const [saving, setSaving] = useState(false);
  const [tester, setTester] = useState({ occ: 60, bar: 100 });
  const [subModal, setSubModal] = useState(null); // { title, content, actions }
  const prevShowRef = useRef(false);
  const prevConfigRef = useRef(config);

  const showToast = (msg, type = "success") => {
    if (type === "error") {
      errorToastMessage(msg);
    } else if (type === "info") {
      infoToastMessage(msg);
    } else {
      successToastMessage(msg);
    }
  };

  const S = useCallback((id) => draft.settings.find((s) => s.id === id), [draft.settings]);
  const chById = useCallback((id) => draft.channels.find((c) => c._id === id), [draft.channels]);
  const dispVal = (s, v) =>
    v == null ? "" : s.type === "pct" ? String(r6(v * 100)) : String(v);

  // Validation
  const { err, warn } = useMemo(() => {
    const errMap = {};
    const warnMap = {};
    const setE = (k, m) => {
      if (!errMap[k]) errMap[k] = m;
    };

    draft.settings.forEach((s) => {
      const v = s.value;
      const k = "s:" + s.id;
      if (s.type === "channel") {
        if (!chById(v)) errMap[k] = "Choose a channel.";
        return;
      }
      if (s.type === "text") {
        if (!String(v || "").trim()) errMap[k] = "Required.";
        else if (String(v).length > 80) errMap[k] = "At most 80 characters.";
        return;
      }
      if (v == null || v === "") {
        errMap[k] = "Required.";
        return;
      }
      if (s.type === "date") {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || isNaN(Date.parse(v)))
          errMap[k] = "Enter a valid date.";
        return;
      }
      if (!isNum(v)) {
        errMap[k] = "Enter a number.";
        return;
      }
      if (s.type === "int" && !Number.isInteger(v))
        errMap[k] = "Whole numbers only.";
      else if (s.min != null && v < s.min)
        errMap[k] = "Must be at least " + fmtVal(draft, s, s.min) + ".";
      else if (s.max != null && v > s.max)
        errMap[k] = "Must be at most " + fmtVal(draft, s, s.max) + ".";
    });

    const n = (id) => {
      const v = S(id)?.value;
      return isNum(v) ? v : null;
    };
    const lt = (a, b) => a != null && b != null && a < b;

    if (lt(n("AnyTightenFcst"), n("HealthyTightenFcst")))
      setE(
        "s:AnyTightenFcst",
        "Must be at or above the Healthy blackout level.",
      );
    if (lt(n("PromoDeep"), n("PromoDesired")))
      setE("s:PromoDeep", "Should not be lower than the desired discount.");
    if (lt(n("PromoDesired"), n("PromoMinDisc")))
      setE(
        "s:PromoDesired",
        "Should not be lower than the minimum discount.",
      );
    if (
      n("PromoMemberDays") != null &&
      n("PromoLastMinDays") != null &&
      n("PromoMemberDays") <= n("PromoLastMinDays")
    ) {
      setE(
        "s:PromoMemberDays",
        "Must be longer than the last-minute window.",
      );
    }
    if (lt(n("ScaleIncr"), n("FixIncr")))
      setE("s:FixIncr", "Should not be higher than the Scale minimum.");

    const w = ["H_ROI", "H_Incr", "H_Leak", "H_Target"].map(n);
    if (
      w.every((v) => v != null) &&
      Math.round(w.reduce((a, b) => a + b, 0)) !== 100
    ) {
      errMap["g:weights"] = "The four weights must add up to 100.";
    }

    const T = draft.hurdleTiers;
    if (!T.length) errMap["g:tiers"] = "Add at least one tier.";
    T.forEach((r, i) => {
      const a = "t:" + r._id + ":minForecast";
      const b = "t:" + r._id + ":hurdlePct";
      if (!isNum(r.minForecast)) errMap[a] = "Required.";
      else if (r.minForecast < 0 || r.minForecast > 1)
        errMap[a] = "Between 0% and 100%.";
      else if (i === 0 && r.minForecast !== 0)
        errMap[a] = "The first tier must start at 0%.";
      else if (
        i > 0 &&
        isNum(T[i - 1].minForecast) &&
        r.minForecast <= T[i - 1].minForecast
      ) {
        errMap[a] = "Must be higher than the row above.";
        errMap["g:tiersOrder"] =
          "Rows must run from the lowest occupancy to the highest.";
      }

      if (!isNum(r.hurdlePct)) errMap[b] = "Required.";
      else if (r.hurdlePct < 0 || r.hurdlePct > 1)
        errMap[b] = "Between 0% and 100%.";
      else if (
        i > 0 &&
        isNum(T[i - 1].hurdlePct) &&
        r.hurdlePct < T[i - 1].hurdlePct
      ) {
        warnMap[b] =
          "Lower than the row above. Usually the hurdle rises with occupancy.";
      }
    });

    const C = draft.channels;
    const seen = {};
    if (!C.length) errMap["g:channels"] = "Add at least one channel.";
    C.forEach((c) => {
      const a = "c:" + c._id + ":name";
      const b = "c:" + c._id + ":commission";
      const nm = (c.name || "").trim().toLowerCase();
      if (!nm) errMap[a] = "Required.";
      else if (seen[nm]) errMap[a] = "Channel names must be unique.";
      seen[nm] = true;
      if (!isNum(c.commission)) errMap[b] = "Required.";
      else if (c.commission < 0 || c.commission > 1)
        errMap[b] = "Between 0% and 100%.";
    });

    C.forEach((c) =>
      NAMEKEYS.forEach(([k]) => {
        if (!String(draft.names[c._id]?.[k] || "").trim()) {
          warnMap["n:" + c._id + ":" + k] =
            "Blank. The campaign will show as unnamed.";
        }
      }),
    );

    if (C.length && !C.some((c) => c.direct)) {
      warnMap["g:channels"] =
        "No direct channel is set, so the parity check (L4) is off.";
    }

    draft.verdicts.forEach((v) => {
      if (!v.nextStep.trim()) warnMap["v:" + v.verdict + ":nextStep"] = "Blank.";
    });

    draft.leakRules.forEach((l) => {
      if (!l.action.trim()) warnMap["l:" + l.code + ":action"] = "Blank.";
    });

    return { err: errMap, warn: warnMap };
  }, [draft, S, chById]);

  const diffs = useMemo(() => diffAll(saved, draft), [saved, draft]);
  const hasErrors = Object.keys(err).length > 0;
  const isDirty = diffs.length > 0;

  // Sync with incoming config when modal opens or server config updates
  useEffect(() => {
    const justOpened = show && !prevShowRef.current;
    const configChanged = show && config !== prevConfigRef.current && !isDirty;
    if (justOpened || configChanged) {
      if (config) {
        const hydrated = hydrateConfig(config);
        setDraft(cloneObj(hydrated));
        setSaved(cloneObj(hydrated));
      }
    }
    prevShowRef.current = show;
    prevConfigRef.current = config;
  }, [show, config, isDirty]);

  // Debounced widget preview update (only when modal is open, dirty, and without errors)
  const previewTimerRef = useRef(null);
  useEffect(() => {
    if (!show || !isDirty || hasErrors || !onPreviewApply) return;
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      try {
        const payload = dehydrateConfig(draft);
        onPreviewApply(payload);
      } catch (e) {
        console.error("Preview apply error:", e);
      }
    }, 400);

    return () => {
      clearTimeout(previewTimerRef.current);
    };
  }, [show, draft, isDirty, hasErrors, onPreviewApply]);

  const updateSetting = (id, val) => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      const s = next.settings.find((x) => x.id === id);
      if (s) s.value = val;
      return next;
    });
  };

  const undoSetting = (id) => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      const s = next.settings.find((x) => x.id === id);
      const o = saved.settings.find((x) => x.id === id);
      if (s && o) s.value = o.value;
      return next;
    });
  };

  // Hurdle tier handlers
  const addTier = () => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      const last = next.hurdleTiers[next.hurdleTiers.length - 1];
      const nr = {
        _id: Date.now() + Math.random(),
        minForecast:
          last && isNum(last.minForecast)
            ? Math.min(1, r6(last.minForecast + 0.05))
            : 0,
        hurdlePct:
          last && isNum(last.hurdlePct)
            ? Math.min(1, r6(last.hurdlePct + 0.05))
            : 0.55,
      };
      next.hurdleTiers.push(nr);
      return next;
    });
  };

  const removeTier = (id) => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      next.hurdleTiers = next.hurdleTiers.filter((r) => r._id !== id);
      return next;
    });
  };

  const sortTiers = () => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      next.hurdleTiers.sort(
        (x, y) =>
          (x.minForecast == null ? 9 : x.minForecast) -
          (y.minForecast == null ? 9 : y.minForecast),
      );
      return next;
    });
  };

  // Channels handlers
  const addChannel = () => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      const newId = Date.now() + Math.random();
      const nr = {
        _id: newId,
        name: "",
        commission: 0.18,
        direct: false,
        dataKey: null,
      };
      next.channels.push(nr);
      next.names[newId] = {
        lastMinute: "",
        memberOnly: "",
        earlyBooker: "",
        valueAdd: "",
      };
      return next;
    });
  };

  const removeChannel = (id) => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      next.channels = next.channels.filter((c) => c._id !== id);
      delete next.names[id];
      const dc = next.settings.find((s) => s.id === "DefaultChannel");
      if (dc && dc.value === id) {
        dc.value = next.channels[0]?._id || null;
      }
      return next;
    });
  };

  const moveChannel = (id, dir) => {
    setDraft((prev) => {
      const next = cloneObj(prev);
      const i = next.channels.findIndex((c) => c._id === id);
      const j = i + dir;
      if (j >= 0 && j < next.channels.length) {
        const t = next.channels[i];
        next.channels[i] = next.channels[j];
        next.channels[j] = t;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (hasErrors || !isDirty || saving) return;
    setSaving(true);
    try {
      const payload = dehydrateConfig(draft);
      if (onSave) {
        await onSave(payload);
      } else {
        showToast("Configuration saved successfully");
      }
      setSaved(cloneObj(draft));
    } catch (e) {
      console.error(e);
      showToast("Could not save. Changes are still in draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSubModal({
      title: "Discard changes?",
      content: (
        <p>
          All unsaved edits will be lost, in this pop-up and in the widget.
        </p>
      ),
      actions: [
        { label: "Keep editing", cls: "ghost", close: true },
        {
          label: "Discard changes",
          cls: "btn",
          close: true,
          fn: () => {
            const restored = cloneObj(saved);
            setDraft(restored);
            if (onPreviewApply) {
              onPreviewApply(dehydrateConfig(restored));
            }
            showToast("Changes discarded", "info");
          },
        },
      ],
    });
  };

  const handleRestoreDefaults = () => {
    setSubModal({
      title: "Restore defaults?",
      content: (
        <p>
          This puts every value back to the original defaults and updates the
          widget. It is not saved until you press <strong>Save changes</strong>.
        </p>
      ),
      actions: [
        { label: "Cancel", cls: "ghost", close: true },
        {
          label: "Restore defaults",
          cls: "btn",
          close: true,
          fn: () => {
            setDraft(cloneObj(defaults));
            showToast("Defaults restored. Review, then save.", "info");
          },
        },
      ],
    });
  };

  const handleReview = () => {
    const groups = {};
    diffs.forEach((d) => {
      groups[d.section] = groups[d.section] || [];
      groups[d.section].push(d);
    });

    const content = (
      <div className="promo-review-container" style={{ padding: "0 2px" }}>
        {/* <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px", marginTop: "2px" }}>
          Review the modified configuration settings before saving changes to the property.
        </p> */}

        {SECTIONS.filter((s) => groups[s.id]).map((s) => (
          <div
            key={s.id}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              marginBottom: "16px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
          >
            {/* Section Header */}
            <div
              style={{
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#3b82f6",
                  }}
                />
                <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1e293b" }}>
                  {s.title}
                </span>
              </div>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#2563eb",
                  background: "#eff6ff",
                  border: "1px solid #dbeafe",
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                {groups[s.id].length} {groups[s.id].length > 1 ? "edits" : "edit"}
              </span>
            </div>

            {/* Change rows */}
            <div style={{ padding: "8px 16px" }}>
              {groups[s.id].map((d, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "10px 0",
                    borderBottom: idx === groups[s.id].length - 1 ? "none" : "1px solid #f1f5f9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div style={{ minWidth: "200px", flex: "1 1 auto" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                      {d.label}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      fontSize: "13px",
                    }}
                  >
                    <span
                      style={{
                        background: "#fef2f2",
                        color: "#991b1b",
                        border: "1px solid #fee2e2",
                        padding: "3px 8px",
                        borderRadius: "5px",
                        fontSize: "12px",
                        fontWeight: 500,
                        textDecoration: "line-through",
                        maxWidth: "260px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={String(d.from)}
                    >
                      {d.from}
                    </span>

                    <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: "13px" }}>→</span>

                    <span
                      style={{
                        background: "#ecfdf5",
                        color: "#047857",
                        border: "1px solid #a7f3d0",
                        padding: "3px 8px",
                        borderRadius: "5px",
                        fontSize: "12px",
                        fontWeight: 700,
                        maxWidth: "280px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={String(d.to)}
                    >
                      {d.to}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );

    setSubModal({
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span>Review unsaved changes</span>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#1d4ed8",
              background: "#dbeafe",
              padding: "2px 10px",
              borderRadius: "12px",
            }}
          >
            {diffs.length} {diffs.length > 1 ? "changes" : "change"}
          </span>
        </div>
      ),
      content,
      actions: [
        {
          label: "Close",
          cls: "btn",
          close: true,
        },
      ],
    });
  };

  // Section badge counts
  const sectionCounts = useMemo(() => {
    const cnt = {};
    const bad = {};
    diffs.forEach((d) => {
      cnt[d.section] = (cnt[d.section] || 0) + 1;
    });
    Object.keys(err).forEach((k) => {
      let sec = null;
      if (k.startsWith("s:")) sec = S(k.slice(2))?.group;
      else if (k.startsWith("t:") || k.includes("tiers")) sec = "hurdle";
      else if (k.startsWith("c:") || k.includes("channels")) sec = "channels";
      else if (k.startsWith("n:")) sec = "names";
      else if (k.startsWith("v:")) sec = "verdicts";
      else if (k.startsWith("l:")) sec = "leaks";
      else if (k === "g:weights") sec = "health";
      if (sec) bad[sec] = (bad[sec] || 0) + 1;
    });
    return { cnt, bad };
  }, [diffs, err, S]);

  if (!show) return null;

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        centered
        className="yield-radar-reservations-modal-container promo-radar-config-modal"
        dialogClassName="yield-radar-reservations-modal-dialog promo-radar-config-dialog"
        contentClassName="yield-radar-reservations-modal-content promo-radar-config-content"
      >
        <Modal.Header closeButton>
          <div className="d-flex align-items-center justify-content-between w-100 pe-3 flex-wrap gap-2">
            <div>
              <Modal.Title id="contained-modal-title-vcenter" style={{ fontSize: "17px", fontWeight: "600", color: "#1f2937", margin: 0 }}>
                Promo Radar configuration
              </Modal.Title>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                Every number in the widget is calculated from these values. Edits show in the widget straight away; press <strong>Save changes</strong> to keep them.
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              {isLoading && (
                <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: "12px" }}>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: "12px", height: "12px" }} />
                  <span>Loading config...</span>
                </div>
              )}
              <div style={{ textAlign: "right", fontSize: "12px", color: "#6b7280", lineHeight: "1.3" }}>
                <strong style={{ color: "#111827" }}>{hotelInfo?.name || "Hotel"}</strong>, {hotelInfo?.capacity || 150} rooms
                <br />
                As of {commonService?.getDateInFormat(hotelInfo?.asOf) || ""}
              </div>
              <Button
                className="secondary-btn"
                type="button"
                onClick={handleRestoreDefaults}
                style={{
                  height: "30px",
                  padding: "0 14px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                }}
              >
                Restore defaults
              </Button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0">
          <div
            className="PromoRadar PromoRadar-cfgDlg-body PromoRadar-cfgDlg"
            style={{
              position: "static",
              transform: "none",
              width: "100%",
              height: "100%",
              maxHeight: "none",
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
              zIndex: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="cbody" style={{ display: "flex", flexDirection: "column", height: "100%", flex: "1 1 auto", overflow: "hidden" }}>
          <div
            className="tabs"
            style={{ flexShrink: 0, position: "sticky", top: 0, zIndex: 10, background: "#ffffff" }}
            role="tablist"
            aria-label="Configuration sections"
          >
            {SECTIONS.map((s) => {
              const bCount = sectionCounts.bad[s.id];
              const mCount = sectionCounts.cnt[s.id];
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === s.id}
                  onClick={() => setActiveTab(s.id)}
                >
                  <span>{s.title}</span>
                  {bCount ? (
                    <i className="nb err" title={`${bCount} problem(s)`}>
                      !
                    </i>
                  ) : mCount ? (
                    <i className="nb mod" title={`${mCount} change(s)`}>
                      {mCount}
                    </i>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="cmain" style={{ flex: "1 1 auto", overflowY: "auto", padding: "18px 22px" }}>
            {SECTIONS.map((s) => {
              if (activeTab !== s.id) return null;
              return (
                <section key={s.id} className="panel">
                  <div className="phead">
                    <h2>{s.title}</h2>
                    <p>{s.desc}</p>
                  </div>

                  {s.id === "hurdle" && (
                    <>
                      <div className="tablewrap">
                        <table className="edit">
                          <thead>
                            <tr>
                              <th>Forecast occupancy from</th>
                              <th className="rangecol">Applies to</th>
                              <th>Hurdle (% of BAR)</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {draft.hurdleTiers.map((r, i) => {
                              const nx = draft.hurdleTiers[i + 1];
                              const rangeText = !isNum(r.minForecast)
                                ? ""
                                : nx &&
                                    isNum(nx.minForecast) &&
                                    nx.minForecast > r.minForecast
                                  ? `${pctTxt(r.minForecast)} to under ${pctTxt(nx.minForecast)}`
                                  : `${pctTxt(r.minForecast)} and above`;
                              const oRow = saved.hurdleTiers.find(
                                (x) => x._id === r._id,
                              );
                              const isMod = !oRow || !sameObj(oRow, r);
                              return (
                                <tr
                                  key={r._id}
                                  className={isMod ? "modified" : ""}
                                >
                                  <td>
                                    <div className="field">
                                      <input
                                        className={`num ${err[`t:${r._id}:minForecast`] ? "invalid" : ""}`}
                                        type="number"
                                        inputMode="decimal"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={dispVal(
                                          { type: "pct" },
                                          r.minForecast,
                                        )}
                                        onChange={(e) => {
                                          const val =
                                            e.target.value === ""
                                              ? null
                                              : r6(
                                                  Number(e.target.value) / 100,
                                                );
                                          setDraft((prev) => {
                                            const next = cloneObj(prev);
                                            const item =
                                              next.hurdleTiers.find(
                                                (x) => x._id === r._id,
                                              );
                                            if (item) item.minForecast = val;
                                            return next;
                                          });
                                        }}
                                      />
                                      <span className="unit">%</span>
                                    </div>
                                    {err[`t:${r._id}:minForecast`] && (
                                      <div className="msg">
                                        {err[`t:${r._id}:minForecast`]}
                                      </div>
                                    )}
                                    <div className="reason rangeinl">
                                      {rangeText}
                                    </div>
                                  </td>
                                  <td className="mut rangecol">
                                    <span>{rangeText}</span>
                                  </td>
                                  <td>
                                    <div className="field">
                                      <input
                                        className={`num ${err[`t:${r._id}:hurdlePct`] ? "invalid" : ""}`}
                                        type="number"
                                        inputMode="decimal"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={dispVal(
                                          { type: "pct" },
                                          r.hurdlePct,
                                        )}
                                        onChange={(e) => {
                                          const val =
                                            e.target.value === ""
                                              ? null
                                              : r6(
                                                  Number(e.target.value) / 100,
                                                );
                                          setDraft((prev) => {
                                            const next = cloneObj(prev);
                                            const item =
                                              next.hurdleTiers.find(
                                                (x) => x._id === r._id,
                                              );
                                            if (item) item.hurdlePct = val;
                                            return next;
                                          });
                                        }}
                                      />
                                      <span className="unit">%</span>
                                    </div>
                                    {err[`t:${r._id}:hurdlePct`] && (
                                      <div className="msg">
                                        {err[`t:${r._id}:hurdlePct`]}
                                      </div>
                                    )}
                                    {warn[`t:${r._id}:hurdlePct`] && (
                                      <div className="msg warn">
                                        {warn[`t:${r._id}:hurdlePct`]}
                                      </div>
                                    )}
                                  </td>
                                  <td className="act">
                                    <button
                                      className="icon-btn"
                                      type="button"
                                      disabled={draft.hurdleTiers.length < 2}
                                      onClick={() => removeTier(r._id)}
                                      aria-label={`Remove tier ${i + 1}`}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="tfoot">
                        <button
                          className="ghost"
                          type="button"
                          onClick={addTier}
                        >
                          + Add tier
                        </button>
                        {err["g:tiersOrder"] && (
                          <button
                            className="ghost"
                            type="button"
                            onClick={sortTiers}
                          >
                            Sort rows
                          </button>
                        )}
                        {err["g:tiers"] && (
                          <div className="msg">{err["g:tiers"]}</div>
                        )}
                        {err["g:tiersOrder"] && (
                          <div className="msg">{err["g:tiersOrder"]}</div>
                        )}
                      </div>
                      <div className="tester">
                        <span>
                          <span className="lbl">Try it:</span>a night forecast at
                        </span>
                        <div className="field">
                          <input
                            className="num"
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            value={tester.occ}
                            onChange={(e) =>
                              setTester({
                                ...tester,
                                occ:
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value),
                              })
                            }
                          />
                          <span className="unit">%</span>
                        </div>
                        <span>with BAR</span>
                        <div className="field">
                          <input
                            className="num"
                            type="number"
                            step="1"
                            min="0"
                            value={tester.bar}
                            onChange={(e) =>
                              setTester({
                                ...tester,
                                bar:
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value),
                              })
                            }
                          />
                          <span className="unit">$</span>
                        </div>
                        <span>
                          has a hurdle of{" "}
                          <b className="num">
                            {(() => {
                              const tiers = draft.hurdleTiers
                                .filter(
                                  (r) =>
                                    isNum(r.minForecast) && isNum(r.hurdlePct),
                                )
                                .sort((a, b) => a.minForecast - b.minForecast);
                              const occDec = tester.occ / 100;
                              let hit = null;
                              tiers.forEach((r) => {
                                if (r.minForecast <= occDec) hit = r;
                              });
                              return hit && isNum(tester.bar)
                                ? `${pctTxt(hit.hurdlePct)} of BAR = $${Math.round(hit.hurdlePct * tester.bar * 100) / 100}`
                                : "—";
                            })()}
                          </b>
                        </span>
                      </div>
                    </>
                  )}

                  {s.id === "channels" && (
                    <>
                      {draft.settings
                        .filter((x) => x.group === "channels")
                        .map((setting) => (
                          <SettingRowItem
                            key={setting.id}
                            setting={setting}
                            savedSetting={saved.settings.find(
                              (x) => x.id === setting.id,
                            )}
                            draftChannels={draft.channels}
                            onUpdate={updateSetting}
                            onUndo={undoSetting}
                            errMsg={err[`s:${setting.id}`]}
                          />
                        ))}
                      <div
                        className="tablewrap"
                        style={{ borderTop: "1px solid var(--line)" }}
                      >
                        <table className="edit">
                          <thead>
                            <tr>
                              <th style={{ width: "45px", textAlign: "center" }}>#</th>
                              <th>Channel</th>
                              <th style={{ width: "160px" }}>Commission</th>
                              <th style={{ width: "150px" }}>Direct channel</th>
                              <th style={{ width: "140px" }}>Data feed</th>
                              <th style={{ width: "105px", textAlign: "right" }} />
                            </tr>
                          </thead>
                          <tbody>
                            {draft.channels.map((c, i) => {
                              const oChannel = saved.channels.find(
                                (x) => x._id === c._id,
                              );
                              const isMod =
                                !oChannel || !sameObj(oChannel, c);
                              const hasFeed = dataKeys?.has(
                                (c.dataKey || c.name || "").trim(),
                              );
                              return (
                                <tr
                                  key={c._id}
                                  className={isMod ? "modified" : ""}
                                >
                                  <td className="ro num" style={{ textAlign: "center" }}>{i + 1}</td>
                                  <td>
                                    <input
                                      className={`tin ${err[`c:${c._id}:name`] ? "invalid" : ""}`}
                                      type="text"
                                      value={c.name}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setDraft((prev) => {
                                          const next = cloneObj(prev);
                                          const target = next.channels.find(
                                            (x) => x._id === c._id,
                                          );
                                          if (target) target.name = v;
                                          return next;
                                        });
                                      }}
                                    />
                                    {err[`c:${c._id}:name`] && (
                                      <div className="msg">
                                        {err[`c:${c._id}:name`]}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <div className="field">
                                      <input
                                        className={`num ${err[`c:${c._id}:commission`] ? "invalid" : ""}`}
                                        type="number"
                                        inputMode="decimal"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={dispVal(
                                          { type: "pct" },
                                          c.commission,
                                        )}
                                        onChange={(e) => {
                                          const v =
                                            e.target.value === ""
                                              ? null
                                              : r6(
                                                  Number(e.target.value) / 100,
                                                );
                                          setDraft((prev) => {
                                            const next = cloneObj(prev);
                                            const target = next.channels.find(
                                              (x) => x._id === c._id,
                                            );
                                            if (target) target.commission = v;
                                            return next;
                                          });
                                        }}
                                      />
                                      <span className="unit">%</span>
                                    </div>
                                    {err[`c:${c._id}:commission`] && (
                                      <div className="msg">
                                        {err[`c:${c._id}:commission`]}
                                      </div>
                                    )}
                                  </td>
                                  <td className="ro">
                                    <label
                                      className="dirlbl"
                                      title="Mark as direct channel"
                                    >
                                      <input
                                        className="ck"
                                        type="checkbox"
                                        checked={c.direct}
                                        onChange={(e) => {
                                          const chk = e.target.checked;
                                          setDraft((prev) => {
                                            const next = cloneObj(prev);
                                            next.channels.forEach(
                                              (x) => (x.direct = false),
                                            );
                                            const target = next.channels.find(
                                              (x) => x._id === c._id,
                                            );
                                            if (target) target.direct = chk;
                                            return next;
                                          });
                                        }}
                                      />
                                      <span>Direct</span>
                                    </label>
                                  </td>
                                  <td className="mut">
                                    <span
                                      className={`chip ${hasFeed ? "perf" : "off"}`}
                                    >
                                      {hasFeed
                                        ? "In data feed"
                                        : "Not in data feed"}
                                    </span>
                                  </td>
                                  <td className="act">
                                    <div className="act-btns">
                                      <button
                                        className="icon-btn sm"
                                        type="button"
                                        title="Move up"
                                        disabled={i === 0}
                                        onClick={() => moveChannel(c._id, -1)}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        className="icon-btn sm"
                                        type="button"
                                        title="Move down"
                                        disabled={i === draft.channels.length - 1}
                                        onClick={() => moveChannel(c._id, 1)}
                                      >
                                        ↓
                                      </button>
                                      <button
                                        className="icon-btn sm del"
                                        type="button"
                                        title="Remove channel"
                                        disabled={draft.channels.length < 2}
                                        onClick={() => removeChannel(c._id)}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="tfoot">
                        <button
                          className="ghost"
                          type="button"
                          onClick={addChannel}
                        >
                          + Add channel
                        </button>
                        {warn["g:channels"] && (
                          <div className="msg warn">{warn["g:channels"]}</div>
                        )}
                      </div>
                    </>
                  )}

                  {s.id === "names" && (
                    <div className="tablewrap">
                      <table className="edit names">
                        <thead>
                          <tr>
                            <th>Channel</th>
                            {NAMEKEYS.map(([k, lab]) => (
                              <th key={k}>{lab}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {draft.channels.map((c) => {
                            const isMod =
                              !saved.names[c._id] ||
                              !sameObj(saved.names[c._id], draft.names[c._id]);
                            return (
                              <tr
                                key={c._id}
                                className={isMod ? "modified" : ""}
                              >
                                <td className="ro">
                                  <strong>{c.name || "(unnamed)"}</strong>
                                </td>
                                {NAMEKEYS.map(([k, lab]) => (
                                  <td key={k}>
                                    <input
                                      className="tin"
                                      type="text"
                                      value={draft.names[c._id]?.[k] || ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setDraft((prev) => {
                                          const next = cloneObj(prev);
                                          if (!next.names[c._id])
                                            next.names[c._id] = {};
                                          next.names[c._id][k] = v;
                                          return next;
                                        });
                                      }}
                                    />
                                    {warn[`n:${c._id}:${k}`] && (
                                      <div className="msg warn">
                                        {warn[`n:${c._id}:${k}`]}
                                      </div>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {s.id === "verdicts" && (
                    <>
                      {draft.settings
                        .filter((x) => x.group === "verdicts")
                        .map((setting) => (
                          <SettingRowItem
                            key={setting.id}
                            setting={setting}
                            savedSetting={saved.settings.find(
                              (x) => x.id === setting.id,
                            )}
                            draftChannels={draft.channels}
                            onUpdate={updateSetting}
                            onUndo={undoSetting}
                            errMsg={err[`s:${setting.id}`]}
                          />
                        ))}
                      <div
                        className="tablewrap"
                        style={{ borderTop: "1px solid var(--line)" }}
                      >
                        <table className="edit">
                          <thead>
                            <tr>
                              <th>Verdict</th>
                              <th>When</th>
                              <th>Next step shown to the user</th>
                            </tr>
                          </thead>
                          <tbody>
                            {draft.verdicts.map((v, i) => {
                              const isMod =
                                v.nextStep !== saved.verdicts[i]?.nextStep;
                              return (
                                <tr
                                  key={v.verdict}
                                  className={isMod ? "modified" : ""}
                                >
                                  <td className="ro">
                                    <span
                                      className={`badge v-${v.verdict.replace(/[^A-Za-z]/g, "")}`}
                                    >
                                      {v.verdict}
                                    </span>{" "}
                                    <span className="code num">{v.rule}</span>
                                  </td>
                                  <td className="mut">{v.when}</td>
                                  <td>
                                    <input
                                      className="tin"
                                      style={{ minWidth: "230px" }}
                                      type="text"
                                      value={v.nextStep}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setDraft((prev) => {
                                          const next = cloneObj(prev);
                                          next.verdicts[i].nextStep = val;
                                          return next;
                                        });
                                      }}
                                    />
                                    {warn[`v:${v.verdict}:nextStep`] && (
                                      <div className="msg warn">
                                        {warn[`v:${v.verdict}:nextStep`]}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {s.id === "health" && (
                    <>
                      {draft.settings
                        .filter((x) => x.group === "health")
                        .map((setting) => (
                          <SettingRowItem
                            key={setting.id}
                            setting={setting}
                            savedSetting={saved.settings.find(
                              (x) => x.id === setting.id,
                            )}
                            draftChannels={draft.channels}
                            onUpdate={updateSetting}
                            onUndo={undoSetting}
                            errMsg={err[`s:${setting.id}`]}
                          />
                        ))}
                      <div className="total">
                        Total{" "}
                        <span
                          className={`pill num ${err["g:weights"] ? "bad" : ""}`}
                        >
                          {["H_ROI", "H_Incr", "H_Leak", "H_Target"].reduce(
                            (a, id) =>
                              a + (isNum(S(id)?.value) ? S(id).value : 0),
                            0,
                          )}
                        </span>
                        {err["g:weights"] && (
                          <div
                            className="msg"
                            style={{ margin: 0, flex: "1 1 200px" }}
                          >
                            {err["g:weights"]}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {s.id === "leaks" && (
                    <div className="tablewrap">
                      <table className="edit">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Name</th>
                            <th>Condition (fixed)</th>
                            <th>Recommended action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {draft.leakRules.map((l, i) => {
                            const isMod =
                              l.action !== saved.leakRules[i]?.action;
                            return (
                              <tr
                                key={l.code}
                                className={isMod ? "modified" : ""}
                              >
                                <td className="ro">
                                  <span className={`badge c-${l.code}`}>
                                    {l.code}
                                  </span>
                                </td>
                                <td className="mut num">{l.name}</td>
                                <td className="mut">{l.condition}</td>
                                <td>
                                  <input
                                    className="tin"
                                    style={{ minWidth: "230px" }}
                                    type="text"
                                    value={l.action}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setDraft((prev) => {
                                        const next = cloneObj(prev);
                                        next.leakRules[i].action = val;
                                        return next;
                                      });
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {s.id !== "hurdle" &&
                    s.id !== "channels" &&
                    s.id !== "names" &&
                    s.id !== "verdicts" &&
                    s.id !== "health" &&
                    s.id !== "leaks" && (
                      <>
                        {draft.settings
                          .filter((x) => x.group === s.id)
                          .map((setting) => (
                            <SettingRowItem
                              key={setting.id}
                              setting={setting}
                              savedSetting={saved.settings.find(
                                (x) => x.id === setting.id,
                              )}
                              draftChannels={draft.channels}
                              onUpdate={updateSetting}
                              onUndo={undoSetting}
                              errMsg={err[`s:${setting.id}`]}
                            />
                          ))}
                      </>
                    )}
                </section>
              );
            })}
          </div>

            </div>
            
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between align-items-center w-100" style={{ padding: "12px 20px", borderTop: "1px solid #eaefff", background: "#ffffff" }}>
          <div className="cbar-in d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
            <div
              className={`status ${hasErrors ? "bad" : isDirty ? "dirty" : "ok"}`}
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px" }}
            >
              <span className="dot" />
              <span>
                {saving
                  ? "Saving…"
                  : hasErrors
                    ? `Fix ${Object.keys(err).length} problem(s). The widget keeps the last valid values.`
                    : isDirty
                      ? `${diffs.length} unsaved change${diffs.length > 1 ? "s" : ""}, previewed live in the widget`
                      : "All changes saved"}
              </span>
            </div>
            <div className="cbar-act d-flex align-items-center gap-2">
              <button
                className="cfg-footer-btn secondary"
                type="button"
                disabled={!isDirty}
                onClick={handleReview}
              >
                Review changes
              </button>
              <button
                className="cfg-footer-btn secondary"
                type="button"
                disabled={saving || !isDirty}
                onClick={handleDiscard}
              >
                Discard
              </button>
              <button
                className="cfg-footer-btn primary"
                type="button"
                disabled={saving || !isDirty || hasErrors}
                onClick={handleSave}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {subModal && (
        <Modal
          show={!!subModal}
          onHide={() => setSubModal(null)}
          centered
          size="lg"
          className="promo-radar-submodal"
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "16px", fontWeight: 600 }}>{subModal.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 20px' }}>{subModal.content}</Modal.Body>
          <Modal.Footer>
            {subModal.actions.map((act, i) => (
              <Button
                key={i}
                className={act.cls === "btn" || act.cls === "primary" ? "default-btn primary-btn" : act.cls === "danger" ? "btn btn-outline-danger" : "secondary-btn"}
                type="button"
                onClick={() => {
                  if (act.fn) act.fn();
                  if (act.close !== false) setSubModal(null);
                }}
              >
                {act.label}
              </Button>
            ))}
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

function SettingRowItem({
  setting,
  savedSetting,
  draftChannels,
  onUpdate,
  onUndo,
  errMsg,
}) {
  const isMod = !sameObj(setting.value, savedSetting?.value);

  const renderInput = () => {
    if (setting.type === "channel") {
      return (
        <div className="field">
          <select
            value={setting.value || ""}
            onChange={(e) => onUpdate(setting.id, Number(e.target.value))}
          >
            {draftChannels.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name || "(unnamed)"}
              </option>
            ))}
          </select>
        </div>
      );
    }
    if (setting.type === "date") {
      return (
        <div className="field">
          <input
            type="date"
            value={setting.value || ""}
            onChange={(e) => onUpdate(setting.id, e.target.value || null)}
          />
        </div>
      );
    }
    if (setting.type === "text") {
      return (
        <div className="field">
          <input
            type="text"
            value={setting.value || ""}
            onChange={(e) => onUpdate(setting.id, e.target.value)}
          />
        </div>
      );
    }

    const step =
      setting.step != null ? setting.step : setting.type === "int" ? 1 : 0.1;
    const mn =
      setting.min != null
        ? setting.type === "pct"
          ? String(r6(setting.min * 100))
          : String(setting.min)
        : "";
    const mx =
      setting.max != null
        ? setting.type === "pct"
          ? String(r6(setting.max * 100))
          : String(setting.max)
        : "";
    const valStr =
      setting.value == null
        ? ""
        : setting.type === "pct"
          ? String(r6(setting.value * 100))
          : String(setting.value);

    return (
      <div className="field">
        <input
          className={`num ${errMsg ? "invalid" : ""}`}
          type="number"
          inputMode="decimal"
          min={mn}
          max={mx}
          step={step}
          value={valStr}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onUpdate(setting.id, null);
            } else {
              const numVal = Number(raw);
              onUpdate(
                setting.id,
                setting.type === "pct" ? r6(numVal / 100) : numVal,
              );
            }
          }}
        />
        <span className="unit">{setting.unit}</span>
      </div>
    );
  };

  return (
    <div className={`srow ${isMod ? "modified" : ""}`}>
      <div className="sinfo">
        <label className="slabel">{setting.label}</label>
        <span className="code num">{setting.id}</span>
        {isMod && <span className="edited">Edited</span>}
        <p className="meaning">{setting.meaning}</p>
        <div className="chips">
          {(setting.usedBy || []).map((k) => (
            <span key={k} className={`chip ${CHIP[k][0]}`}>
              {CHIP[k][1]}
            </span>
          ))}
          {setting.isNew && <span className="chip new">New setting</span>}
          {setting.notUsed && <span className="chip off">Not used yet</span>}
        </div>
      </div>
      <div className="sctl">
        {renderInput()}
        {isMod && (
          <button
            className="icon-btn"
            type="button"
            title="Reset to saved value"
            onClick={() => onUndo(setting.id)}
          >
            ↺
          </button>
        )}
        {errMsg && <div className="msg">{errMsg}</div>}
      </div>
    </div>
  );
}